import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Eye, Plus, X, Upload, Download, Search, Edit2, RefreshCw, ZoomIn, ZoomOut, RotateCcw, FileSpreadsheet, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import '../../Style/DocMasterView.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DocMasterService from '../../Api/DocMasterService';
import { toast } from 'react-toastify';
import { getDateString, ServiceProvider } from '../../Api/ServiceProvider';
import permissionService from '../../Permission/Permission';
import { useHistoryList } from '../Breadcrum/HistoryProvider';
import Breadcrumbs from '../Breadcrum/BreadcrumbHistory';
import UserService from '../../Api/UserService';
import pdiExcel from '../../Assets/templates/pdi_template.xlsx';
import buildupExcel from '../../Assets/templates/buildup_template.xlsx';
import testExcel from '../../Assets/templates/test_template.xlsx';
import SecureImage from '../ImgService/loadImage';
import { uploadModalProps } from '../../Const/constant';

// =============================================
// FULL-SCREEN ZOOMABLE IMAGE VIEWER
// =============================================
const FullScreenImageViewer = ({ isOpen, onClose, images, initialIndex, viewType, userData }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const MIN_SCALE = 1;
    const MAX_SCALE = 5;
    const ZOOM_STEP = 0.5;

    useEffect(() => {
        setCurrentIndex(initialIndex);
        resetZoom();
    }, [initialIndex, isOpen]);

    useEffect(() => {
        resetZoom();
    }, [currentIndex]);

    const resetZoom = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const zoomIn = (e) => {
        e?.stopPropagation();
        setScale(prev => Math.min(prev + ZOOM_STEP, MAX_SCALE));
    };

    const zoomOut = (e) => {
        e?.stopPropagation();
        setScale(prev => {
            const next = prev - ZOOM_STEP;
            if (next <= MIN_SCALE) {
                setPosition({ x: 0, y: 0 });
                return MIN_SCALE;
            }
            return next;
        });
    };

    // Mouse wheel zoom
    const handleWheel = (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            setScale(prev => Math.min(prev + 0.15, MAX_SCALE));
        } else {
            setScale(prev => {
                const next = prev - 0.15;
                if (next <= MIN_SCALE) {
                    setPosition({ x: 0, y: 0 });
                    return MIN_SCALE;
                }
                return next;
            });
        }
    };

    // Drag to pan when zoomed in
    const handleMouseDown = (e) => {
        if (scale > 1) {
            e.preventDefault();
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => setIsDragging(false);

    // Touch support (pinch-to-zoom & pan)
    const lastTouchDistRef = useRef(null);

    const handleTouchStart = (e) => {
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            lastTouchDistRef.current = Math.sqrt(dx * dx + dy * dy);
        } else if (e.touches.length === 1 && scale > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - position.x,
                y: e.touches[0].clientY - position.y
            });
        }
    };

    const handleTouchMove = (e) => {
        e.preventDefault();
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (lastTouchDistRef.current !== null) {
                const delta = dist - lastTouchDistRef.current;
                setScale(prev => {
                    const next = prev + delta * 0.01;
                    if (next <= MIN_SCALE) { setPosition({ x: 0, y: 0 }); return MIN_SCALE; }
                    return Math.min(next, MAX_SCALE);
                });
            }
            lastTouchDistRef.current = dist;
        } else if (e.touches.length === 1 && isDragging && scale > 1) {
            setPosition({
                x: e.touches[0].clientX - dragStart.x,
                y: e.touches[0].clientY - dragStart.y
            });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        lastTouchDistRef.current = null;
    };

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    if (!isOpen || images.length === 0) return null;

    return (
        <div
            className="fullscreen-image-viewer"
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.92)',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {/* Top toolbar */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 16px',
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                    zIndex: 10,
                }}
            >
                {/* Counter */}
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: '500' }}>
                    {currentIndex + 1} / {images.length}
                </span>

                {/* Zoom controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                        onClick={zoomOut}
                        disabled={scale <= MIN_SCALE}
                        style={{
                            background: scale <= MIN_SCALE ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                            border: 'none',
                            borderRadius: '6px',
                            color: scale <= MIN_SCALE ? 'rgba(255,255,255,0.3)' : '#fff',
                            padding: '6px 10px',
                            cursor: scale <= MIN_SCALE ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s',
                        }}
                        title="Zoom Out"
                    >
                        <ZoomOut size={16} />
                    </button>

                    <span style={{
                        color: '#fff',
                        fontSize: '13px',
                        minWidth: '44px',
                        textAlign: 'center',
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: '6px',
                        padding: '4px 8px',
                    }}>
                        {Math.round(scale * 100)}%
                    </span>

                    <button
                        onClick={zoomIn}
                        disabled={scale >= MAX_SCALE}
                        style={{
                            background: scale >= MAX_SCALE ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                            border: 'none',
                            borderRadius: '6px',
                            color: scale >= MAX_SCALE ? 'rgba(255,255,255,0.3)' : '#fff',
                            padding: '6px 10px',
                            cursor: scale >= MAX_SCALE ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s',
                        }}
                        title="Zoom In"
                    >
                        <ZoomIn size={16} />
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); resetZoom(); }}
                        disabled={scale === 1}
                        style={{
                            background: scale === 1 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                            border: 'none',
                            borderRadius: '6px',
                            color: scale === 1 ? 'rgba(255,255,255,0.3)' : '#fff',
                            padding: '6px 10px',
                            cursor: scale === 1 ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'all 0.2s',
                        }}
                        title="Reset Zoom"
                    >
                        <RotateCcw size={15} />
                    </button>
                </div>

                {/* Close */}
                <button
                    onClick={onClose}
                    style={{
                        background: 'rgba(255,255,255,0.15)',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        padding: '6px 10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background 0.2s',
                    }}
                    title="Close"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Main image area */}
            <div
                onClick={(e) => e.stopPropagation()}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                    userSelect: 'none',
                    padding: '60px 80px',
                    boxSizing: 'border-box',
                }}
            >
                <div
                    style={{
                        transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                        transformOrigin: 'center center',
                        transition: isDragging ? 'none' : 'transform 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '100%',
                        maxHeight: '100%',
                    }}
                >
                    <SecureImage
                        url={`${ServiceProvider.assert.get}images/${viewType}_master/${images[currentIndex]}`}
                        token={userData?.accessToken}
                        alt={`Image ${currentIndex + 1}`}
                        style={{
                            maxWidth: '100%',
                            maxHeight: 'calc(100vh - 140px)',
                            objectFit: 'contain',
                            borderRadius: '4px',
                            display: 'block',
                            pointerEvents: 'none',
                        }}
                    />
                </div>
            </div>

            {/* Navigation arrows */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={handlePrev}
                        style={{
                            position: 'absolute',
                            left: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(255,255,255,0.15)',
                            border: 'none',
                            borderRadius: '50%',
                            color: '#fff',
                            width: '44px',
                            height: '44px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            zIndex: 10,
                            backdropFilter: 'blur(4px)',
                            transition: 'background 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                        title="Previous"
                    >
                        &#10094;
                    </button>

                    <button
                        onClick={handleNext}
                        style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'rgba(255,255,255,0.15)',
                            border: 'none',
                            borderRadius: '50%',
                            color: '#fff',
                            width: '44px',
                            height: '44px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            zIndex: 10,
                            backdropFilter: 'blur(4px)',
                            transition: 'background 0.2s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                        title="Next"
                    >
                        &#10095;
                    </button>
                </>
            )}

            {/* Thumbnail strip */}
            {images.length > 1 && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '10px 16px',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                        overflowX: 'auto',
                    }}
                >
                    {images.map((img, idx) => (
                        <div
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '4px',
                                border: idx === currentIndex ? '2px solid #4fc3f7' : '2px solid transparent',
                                opacity: idx === currentIndex ? 1 : 0.5,
                                cursor: 'pointer',
                                flexShrink: 0,
                                overflow: 'hidden',
                                transition: 'all 0.2s',
                            }}
                        >
                            <SecureImage
                                url={`${ServiceProvider.assert.get}images/${viewType}_master/${img}`}
                                token={userData?.accessToken}
                                alt={`Thumb ${idx + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Hint */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'absolute',
                    bottom: images.length > 1 ? '68px' : '12px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: '11px',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                }}
            >
                Scroll to zoom · Drag to pan when zoomed
            </div>
        </div>
    );
};

// Custom hook for drag and drop functionality - UPDATED FOR EXCEL ONLY
const useDragAndDrop = (onDrop) => {
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);

    const isExcelFile = (file) => {
        return file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'));
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const files = Array.from(e.dataTransfer.files);
            // Filter only Excel files
            const excelFiles = files.filter(isExcelFile);

            if (excelFiles.length === 0) {
                toast.error("Only Excel files (.xlsx, .xls) are allowed");
            } else if (onDrop && typeof onDrop === 'function') {
                onDrop(excelFiles);
            }
        }
        dragCounter.current = 0;
    };

    return { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop };
};

// Global Loading Overlay Component
const GlobalLoadingOverlay = ({ isLoading, message }) => {
    if (!isLoading) return null;

    return (
        <div className="global-loading-overlay">
            <div className="global-loading-spinner">
                <div className="spinner"></div>
                <p>{message || "Processing..."}</p>
            </div>
        </div>
    );
};

// Image Display Popup Component
const ImageDisplayPopup = React.memo(({ userData, isOpen, onClose, pdiItem }) => {
    if (!isOpen || !pdiItem) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                <div className="modal-header">
                    <h3>Images for {pdiItem.pn}</h3>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="modal-content">
                    {pdiItem.images && pdiItem.images.length > 0 ? (
                        <div className="image-gallery-grid">
                            {pdiItem.images.map((imageName, index) => {
                                const imageUrl = `${ServiceProvider.assert.get}images/pdi_master/${imageName}`;
                                return (
                                    <div key={index} className="image-gallery-item">
                                        <SecureImage
                                            url={imageUrl}
                                            token={userData.accessToken}
                                            alt={``}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="no-data" style={{ padding: '40px' }}>
                            No images available for this item.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

// SearchBox Component
const SearchBox = ({ value, onChange, onClear, placeholder }) => {
    return (
        <div className="search-box">
            <Search size={16} />
            <input
                type="text"
                placeholder={placeholder || "Search..."}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
            {value && (
                <X size={16} className="search-clear-btn" onClick={onClear} />
            )}
        </div>
    );
};

const DocMasterListView = () => {
    const [docList, setDocList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [userData, setUserData] = useState(null);

    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showReplaceModal, setShowReplaceModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [showImageDisplayPopup, setShowImageDisplayPopup] = useState(false);
    const [selectedPdiItemForImages, setSelectedPdiItemForImages] = useState(null);

    const [fullScreenViewer, setFullScreenViewer] = useState({
        isOpen: false,
        images: [],
        initialIndex: 0,
        viewType: 'pdi'
    });

    const [globalLoading, setGlobalLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");

    const [searchTerm, setSearchTerm] = useState('');
    const [uploadType, setUploadType] = useState('buildup');
    const [uploadTargetDoc, setUploadTargetDoc] = useState(null);
    const [showScrollIndicator, setShowScrollIndicator] = useState(true);

    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canExport, setCanExport] = useState(false);
    const [canUpload, setCanUpload] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);

    const [viewType, setViewType] = useState(null);
    const [detailPagination, setDetailPagination] = useState({
        buildup: { searchTerm: '', currentPage: 1, itemsPerPage: 25 },
        test: { searchTerm: '', currentPage: 1, itemsPerPage: 25 },
        pdi: { searchTerm: '', currentPage: 1, itemsPerPage: 25 },
    });

    const [showCompletePopup, setShowCompletePopup] = useState(false);
    const [completeData, setCompleteData] = useState(null);

    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [deleteData, setDeleteData] = useState(null);

    const [uploadModalFile, setUploadModalFile] = useState(null);
    const [replaceModalFile, setReplaceModalFile] = useState(null);
    const [createModalFile, setCreateModalFile] = useState(null);
    const [createModalType, setCreateModalType] = useState('buildup');

    const [uploadModalError, setUploadModalError] = useState(null);
    const [replaceModalError, setReplaceModalError] = useState(null);
    const [createModalError, setCreateModalError] = useState(null);

    // Refs for stable references
    const userDataRef = useRef(null);
    const fileInputRefs = {
        upload: useRef(null),
        replace: useRef(null),
        create: useRef(null)
    };

    // Use refs to track initialization state to prevent double calls
    const isInitializedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    // Track previous search term to detect clearing
    const prevSearchTermRef = useRef('');

    const handleCloseImageDisplayPopup = () => {
        setShowImageDisplayPopup(false);
        setSelectedPdiItemForImages(null);
    };

    const handleViewImages = (item, imageType = 'images') => {
        const images = item[imageType] || [];
        if (images.length > 0) {
            setFullScreenViewer({
                isOpen: true,
                images: images,
                initialIndex: 0,
                viewType: viewType
            });
        }
    };

    const handleCloseFullScreenViewer = () => {
        setFullScreenViewer({ isOpen: false, images: [], initialIndex: 0, viewType: 'pdi' });
    };

    const handleDeleteCancel = () => {
        setShowDeletePopup(false);
        setDeleteData(null);
    };

    const handleCompleteCancel = () => {
        setShowCompletePopup(false);
        setCompleteData(null);
    };

    const { addCustomPath, removeCustomPath } = useHistoryList();

    const backNav = (item) => {
        handleBackToList();
    };

    // Scroll indicator effect
    useEffect(() => {
        const handleScroll = () => {
            const tableWrapper = document.querySelector('.bom-table-wrapper');
            if (tableWrapper) {
                const { scrollLeft, scrollWidth, clientWidth } = tableWrapper;
                if (scrollLeft > 0 || scrollWidth <= clientWidth) {
                    setShowScrollIndicator(false);
                } else {
                    setShowScrollIndicator(true);
                }
            }
        };

        const tableWrapper = document.querySelector('.bom-table-wrapper');
        if (tableWrapper) {
            tableWrapper.addEventListener('scroll', handleScroll);
            handleScroll();
            return () => tableWrapper.removeEventListener('scroll', handleScroll);
        }
    }, [selectedDoc, docList]);

    // SINGLE INITIALIZATION EFFECT - Consolidated all initialization logic
    useEffect(() => {
        // Prevent double execution in React StrictMode
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;

        const user = JSON.parse(localStorage.getItem("user"));
        setUserData(user);
        userDataRef.current = user;

        if (user) {
            const hasView = permissionService.hasPermission('docmaster', 'view');
            const hasAdd = permissionService.hasPermission('docmaster', 'add');
            const hasEdit = permissionService.hasPermission('docmaster', 'edit');

            setCanView(hasView);
            setCanAdd(hasAdd);
            setCanEdit(hasEdit);
            setCanUpload(hasEdit); // Upload uses edit permission

            // Fetch users only once
            fetchUsers(user);

            if (!hasView) {
                toast.error("You don't have permission to access the DOC Master module", {
                    autoClose: 5000,
                    position: "top-center"
                });
            }
        }
    }, []); // Empty dependency array - runs once only

    // Memoized fetch function with stable reference
    const fetchDocMasters = useCallback(async (page, search = '', isSearchMode = false) => {
        // Prevent concurrent calls
        if (isFetchingRef.current) return;

        if (!canView) return;

        isFetchingRef.current = true;
        setLoading(true);

        try {
            const response = await DocMasterService.getAll(page, itemsPerPage, search);
            if (response && response.data.length > 0) {
                setDocList(response.data || []);
                setTotalCount(response.count || 0);
            } else {
                setDocList([]);
                setTotalCount(0);
                toast.info('No records found', { autoClose: 3000 });
            }
        } catch (err) {
            setDocList([]);
            setTotalCount(0);
            if (!isSearchMode) {
                toast.error(err?.error || "Unable to fetch DOC Masters", { autoClose: 3000 });
            }
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [itemsPerPage, canView]); // Only depend on stable values

    // Initial data fetch - only after permissions are set
    useEffect(() => {
        if (!canView || !userDataRef.current) return;

        // Only fetch on initial load (page 1, no search)
        if (currentPage === 1 && !searchTerm && !isFetchingRef.current) {
            fetchDocMasters(1, '', false);
        }
    }, [canView, fetchDocMasters]); // Removed currentPage and searchTerm to prevent double calls

    // Handle search with debounce - SEPARATED LOGIC FOR SEARCH vs CLEAR
    useEffect(() => {
        if (!canView || !userDataRef.current) return;

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        const currentSearch = searchTerm.trim();
        const prevSearch = prevSearchTermRef.current.trim();

        // CASE 1: Search was cleared (had value before, now empty) - fetch normal data
        if (currentSearch === '' && prevSearch.length > 0) {
            setCurrentPage(1);
            fetchDocMasters(1, '', false);
            prevSearchTermRef.current = '';
            return;
        }

        // CASE 2: Search term has less than 3 chars - don't hit API, just update ref
        if (currentSearch.length > 0 && currentSearch.length < 3) {
            prevSearchTermRef.current = currentSearch;
            return;
        }

        // CASE 3: Search term has 3+ chars - debounce and hit API
        if (currentSearch.length >= 3) {
            searchTimeoutRef.current = setTimeout(() => {
                setCurrentPage(1);
                fetchDocMasters(1, currentSearch, true);
                prevSearchTermRef.current = currentSearch;
            }, 500);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, canView, fetchDocMasters]);

    // Handle page changes (not initial load)
    useEffect(() => {
        if (!canView || !userDataRef.current) return;
        if (currentPage === 1 && !searchTerm) return; // Skip initial load

        // Only fetch if we have valid search (3+ chars) or no search
        const currentSearch = searchTerm.trim();
        if (currentSearch.length === 0 || currentSearch.length >= 3) {
            fetchDocMasters(currentPage, currentSearch, currentSearch.length >= 3);
        }
    }, [currentPage]); // Only trigger on page changes

    const totalPages = useMemo(() => Math.ceil(totalCount / itemsPerPage) || 1, [totalCount, itemsPerPage]);

    const fetchUsers = async (userData) => {
        try {
            const data = await UserService.getAll(null, null, searchTerm, userData.accessToken);
            setUserList(data.users);
        } catch (error) {
            setUserList([]);
        }
    };

    const handleViewSection = (doc, type) => {
        if (!canView) {
            toast.error("You don't have permission to view documents");
            return;
        }
        setSelectedDoc(doc);
        setViewType(type);
        setDetailPagination(prev => ({
            ...prev,
            [type]: { searchTerm: '', currentPage: 1, itemsPerPage: 25 }
        }));
    };

    const handleBackToList = () => {
        setSelectedDoc(null);
        setViewType(null);
    };

    const openUploadModal = useCallback((doc, type) => {
        if (!canUpload) {
            toast.error("You don't have permission to upload documents");
            return;
        }
        setUploadTargetDoc(doc);
        setUploadType(type);
        setUploadModalFile(null);
        setUploadModalError(null);
        setShowUploadModal(true);
    }, [canUpload]);

    const openDetailReplaceModal = useCallback((type) => {
        if (!canUpload) {
            toast.error("You don't have permission to replace documents");
            return;
        }
        setUploadTargetDoc(selectedDoc);
        setUploadType(type);
        setReplaceModalFile(null);
        setReplaceModalError(null);
        setShowReplaceModal(true);
    }, [canUpload, selectedDoc]);

    const toggleMandatedInList = useCallback(async (doc, type) => {
        if (!canEdit) {
            toast.error("You don't have permission to edit documents");
            return;
        }
        try {
            const updated = { ...doc };
            if (type === 'buildup') updated.buildup_mandated = !updated.buildup_mandated;
            else if (type === 'test') updated.test_mandated = !updated.test_mandated;
            else if (type === 'pdi') updated.pdi_mandated = !updated.pdi_mandated;

            let docId = doc?._id;
            await DocMasterService.update(docId, { docMasterUpdate: { [`${type}_mandated`]: updated[`${type}_mandated`] } }, userData.uid);
            setDocList(prev => prev.map(d => d._id === doc._id ? updated : d));
        } catch (e) {
            toast.error(`${e?.message || e?.error?.message || e?.error || 'Unable to process your request'}`);
        }
    }, [canEdit, userData]);

    const docStatusUpdate = useCallback(async () => {
        if (!canEdit) {
            toast.error("You don't have permission to edit documents");
            return;
        }
        try {
            const updated = { ...completeData };
            updated.status = updated.status === 'active' ? 'inactive' : 'active';
            let docId = completeData?._id;
            await DocMasterService.update(docId, { docMasterUpdate: { status: updated.status } }, userData.uid);
            setDocList(prev => prev.map(d => d._id === completeData._id ? updated : d));
            handleCompleteCancel();
        } catch (e) {
            toast.error(`${e?.message || e?.error?.message || e?.error || 'Unable to process your request'}`);
        }
    }, [canEdit, userData, completeData]);

    const docDelete = async () => {
        try {
            let docId = deleteData?._id;
            await DocMasterService.delete(docId);
            setDeleteData(null);
            setShowDeletePopup(false);
            await fetchDocMasters();
            toast.success("Document deleted successfully");
        } catch (e) {
            toast.error(`${e?.message || e?.error?.message || e?.error || 'Unable to process your request'}`);
        }
    }

    const togglePdiRemarkUpdate = useCallback(async (selectedItem, data, type) => {
        try {
            const remarkFieldMap = { pdi: "rm_man", buildup: "remark_m", test: "remark_m" };
            const remarkField = remarkFieldMap[type] || "remark_m";
            const updateKey = `${type}Update`;
            const idField = `${type}_id`;
            const itemsKey = `${type}_items`;

            const response = await DocMasterService.update(
                selectedDoc._id,
                { [updateKey]: [{ [idField]: selectedItem[idField], [remarkField]: data }] },
                userData.uid
            );

            if (response && response.success) {
                const updatedDoc = { ...selectedDoc };
                const items = updatedDoc[itemsKey] || [];
                const itemIndex = items.findIndex((item) => item[`${type}_id`] === selectedItem[`${type}_id`]);

                if (itemIndex !== -1) {
                    items[itemIndex][remarkField] = data;
                    updatedDoc[itemsKey] = items;
                    setSelectedDoc(updatedDoc);
                    setDocList((prev) => prev.map((doc) => doc._id === selectedDoc._id ? updatedDoc : doc));
                }
            } else {
                toast.error(`❌ ${uploadModalProps[type]} remark update failed:`, response);
            }
        } catch (error) {
            toast.error(`Error updating ${type} remark:`, error?.message || error?.error?.message || error?.error || '');
        }
    }, [selectedDoc, userData]);

    const togglePdiVerifyUpdate = useCallback(async (selectedItem, data, type) => {
        try {
            const remarkFieldMap = { pdi: "verify_m" };
            const remarkField = remarkFieldMap[type] || "verify_m";
            const updateKey = `${type}Update`;
            const idField = `${type}_id`;
            const itemsKey = `${type}_items`;

            const response = await DocMasterService.update(
                selectedDoc._id,
                { [updateKey]: [{ [idField]: selectedItem[idField], [remarkField]: data }] },
                userData.uid
            );

            if (response && response.success) {
                const updatedDoc = { ...selectedDoc };
                const items = updatedDoc[itemsKey] || [];
                const itemIndex = items.findIndex((item) => item[`${type}_id`] === selectedItem[`${type}_id`]);

                if (itemIndex !== -1) {
                    items[itemIndex][remarkField] = data;
                    updatedDoc[itemsKey] = items;
                    setSelectedDoc(updatedDoc);
                    setDocList((prev) => prev.map((doc) => doc._id === selectedDoc._id ? updatedDoc : doc));
                }
            } else {
                toast.error(`❌ ${uploadModalProps[type]} verify update failed:`, response);
            }
        } catch (error) {
            toast.error(`Error updating ${type} verify:`, error?.message || error?.error?.message || error?.error || '');
        }
    }, [selectedDoc, userData]);

    const updateDetailPagination = useCallback((type, updates) => {
        setDetailPagination(prev => ({ ...prev, [type]: { ...prev[type], ...updates } }));
    }, []);

    const handleFirstPage = useCallback(() => setCurrentPage(1), []);
    const handlePreviousPage = useCallback(() => setCurrentPage(prev => Math.max(1, prev - 1)), []);
    const handleNextPage = useCallback(() => setCurrentPage(prev => Math.min(totalPages, prev + 1)), [totalPages]);
    const handleLastPage = useCallback(() => setCurrentPage(totalPages), [totalPages]);
    const handlePageSizeChange = useCallback((e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }, []);

    const handlePdiApiCall = useCallback(async (file, docMasterId = null, isReplace = false) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (docMasterId) formData.append('doc_master_id', docMasterId);
            if (isReplace) formData.append('replace', 'true');

            const response = await fetch(ServiceProvider.docMaster.createPdi, {
                method: 'POST',
                body: formData,
                headers: { 'Authorization': `Bearer ${userData.accessToken}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'API request failed');
            }
            return await response.json();
        } catch (error) {
            console.error('PDI API call error:', error);
            throw error;
        }
    }, [userData]);

    const handleBuildupApiCall = useCallback(async (file, docMasterId = null, isReplace = false) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (docMasterId) formData.append('doc_master_id', docMasterId);
            if (isReplace) formData.append('replace', 'true');

            const response = await fetch(`${ServiceProvider.docMaster.createBuildup}`, {
                method: 'POST',
                body: formData,
                headers: { 'Authorization': `Bearer ${userData.accessToken}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'API request failed');
            }
            return await response.json();
        } catch (error) {
            console.error('Buildup API call error:', error);
            throw error;
        }
    }, [userData]);

    const handleTestApiCall = useCallback(async (file, docMasterId = null, isReplace = false) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (docMasterId) formData.append('doc_master_id', docMasterId);
            if (isReplace) formData.append('replace', 'true');

            const response = await fetch(`${ServiceProvider.docMaster.createTest}`, {
                method: 'POST',
                body: formData,
                headers: { 'Authorization': `Bearer ${userData.accessToken}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'API request failed');
            }
            return await response.json();
        } catch (error) {
            console.error('Test API call error:', error);
            throw error;
        }
    }, [userData]);

    const downloadTemplate = useCallback(async (templateType) => {
        if (!canView) {
            toast.error("You don't have permission to download templates");
            return;
        }
        try {
            let templateFile;
            switch (templateType) {
                case "pdi": templateFile = pdiExcel; break;
                case "buildup": templateFile = buildupExcel; break;
                case "test": templateFile = testExcel; break;
                default: throw new Error('Invalid template type');
            }

            const response = await fetch(templateFile);
            if (!response.ok) throw new Error('Template file not found');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${uploadModalProps[templateType]}_template.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading template:', error);
            toast.error('Failed to download template: ' + error.message);
        }
    }, [canView]);

    const handleUploadConfirm = useCallback(async (file, type) => {
        if (!file || !canUpload) return;

        setGlobalLoading(true);
        setLoadingMessage(`Uploading ${uploadModalProps[type].toUpperCase()} data...`);

        try {
            let response;
            if (type === 'pdi') response = await handlePdiApiCall(file, uploadTargetDoc._id, false);
            else if (type === 'buildup') response = await handleBuildupApiCall(file, uploadTargetDoc._id, false);
            else if (type === 'test') response = await handleTestApiCall(file, uploadTargetDoc._id, false);

            if (response && response.data) {
                toast.success(`${uploadModalProps[type]} items uploaded successfully!`);
                const updatedDoc = { ...uploadTargetDoc };
                updatedDoc[`${type}_items`] = response.data || [];
                setDocList(prev => prev.map(doc => doc._id === uploadTargetDoc._id ? updatedDoc : doc));
                if (selectedDoc && selectedDoc._id === uploadTargetDoc._id) setSelectedDoc(updatedDoc);

                // Refresh current view
                const currentSearch = searchTerm.trim();
                await fetchDocMasters(currentPage, currentSearch, currentSearch.length >= 3);

                setShowUploadModal(false);
                setUploadTargetDoc(null);
                setUploadModalFile(null);
                setUploadModalError(null);
            } else {
                throw new Error(response.message || 'Upload failed');
            }
        } catch (err) {
            console.error('Upload error:', err);
            toast.error(err.message || 'Upload failed: Invalid file format or server error');
        } finally {
            setGlobalLoading(false);
            setLoadingMessage("");
        }
    }, [canUpload, userData, uploadTargetDoc, selectedDoc, currentPage, searchTerm, handlePdiApiCall, handleBuildupApiCall, handleTestApiCall, fetchDocMasters]);

    const handleReplaceConfirm = useCallback(async (file, type) => {
        if (!file || !canUpload) return;

        setGlobalLoading(true);
        setLoadingMessage(`Replacing ${uploadModalProps[type]} data...`);

        try {
            let response;
            if (type === 'pdi') response = await handlePdiApiCall(file, uploadTargetDoc._id, true);
            else if (type === 'buildup') response = await handleBuildupApiCall(file, uploadTargetDoc._id, true);
            else if (type === 'test') response = await handleTestApiCall(file, uploadTargetDoc._id, true);

            if (response && response.data) {
                toast.success(`${uploadModalProps[type]} items replaced successfully!`);
                const updatedDoc = { ...uploadTargetDoc };
                updatedDoc[`${type}_items`] = response.data || [];
                updatedDoc[`${type}_desc`] = response.desc || [];
                setDocList(prev => prev.map(doc => doc._id === uploadTargetDoc._id ? updatedDoc : doc));
                if (selectedDoc && selectedDoc._id === uploadTargetDoc._id) setSelectedDoc(updatedDoc);

                // Refresh current view
                const currentSearch = searchTerm.trim();
                await fetchDocMasters(currentPage, currentSearch, currentSearch.length >= 3);

                setShowReplaceModal(false);
                setUploadTargetDoc(null);
                setReplaceModalFile(null);
                setReplaceModalError(null);
            } else {
                throw new Error(response.message || 'Replace failed');
            }
        } catch (err) {
            console.error('Replace error:', err);
            toast.error(err.message || 'Replace failed: Invalid file format or server error');
        } finally {
            setGlobalLoading(false);
            setLoadingMessage("");
        }
    }, [canUpload, userData, uploadTargetDoc, selectedDoc, currentPage, searchTerm, handlePdiApiCall, handleBuildupApiCall, handleTestApiCall, fetchDocMasters]);

    const handleCreate = useCallback(async (file, type) => {
        if (!canAdd || !file) {
            if (!file) toast.error('Please select a file');
            return;
        }

        setGlobalLoading(true);
        setLoadingMessage(`Creating document with ${uploadModalProps[type]} data...`);

        try {
            let response;
            if (type === 'pdi') response = await handlePdiApiCall(file, null, false);
            else if (type === 'buildup') response = await handleBuildupApiCall(file, null, false);
            else if (type === 'test') response = await handleTestApiCall(file, null, false);

            if (response && response.data) {
                toast.success(`Document created with ${uploadModalProps[type]} items!`);

                // Refresh current view
                const currentSearch = searchTerm.trim();
                await fetchDocMasters(currentPage, currentSearch, currentSearch.length >= 3);

                setShowCreateModal(false);
                setCreateModalFile(null);
                setCreateModalError(null);
                setCreateModalType('buildup');
            } else {
                throw new Error(response.message || 'Create failed');
            }
        } catch (err) {
            console.error('Create document error:', err);
            const errorMessage = err.message || err.error || 'Failed to create document';
            setCreateModalError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setGlobalLoading(false);
            setLoadingMessage("");
        }
    }, [canAdd, userData, currentPage, searchTerm, handlePdiApiCall, handleBuildupApiCall, handleTestApiCall, fetchDocMasters]);

    const getDetailItems = () => selectedDoc?.[`${viewType}_items`] || [];

    const getFilteredDetailList = () => {
        const items = getDetailItems();
        const current = detailPagination[viewType];
        if (!current?.searchTerm) return items;

        const term = current.searchTerm.toLowerCase();
        if (viewType === 'pdi') {
            return items.filter(i =>
                i.pn?.toLowerCase().includes(term) ||
                i.desc?.toLowerCase().includes(term) ||
                i.sz?.toLowerCase().includes(term)
            );
        } else {
            return items.filter(i =>
                i.inspection_parameter?.toLowerCase().includes(term) ||
                i.specification?.toLowerCase().includes(term) ||
                i.measuring_instrument?.toLowerCase().includes(term)
            );
        }
    };

    const getPaginatedDetailList = () => {
        const filtered = getFilteredDetailList();
        const { currentPage, itemsPerPage } = detailPagination[viewType];
        const start = (currentPage - 1) * itemsPerPage;
        return filtered.slice(start, start + itemsPerPage);
    };

    const getDetailTotalPages = () => {
        const filtered = getFilteredDetailList();
        const { itemsPerPage } = detailPagination[viewType];
        return Math.ceil(filtered.length / itemsPerPage) || 1;
    };

    const exportToExcel = useCallback(async (data, viewType) => {
        try {
            let fileUrl;
            switch (viewType) {
                case 'pdi':
                case 'buildup':
                case 'test':
                    fileUrl = `${ServiceProvider.assert.downloadPdiExcel}${data[`${viewType}_excel_file`]}`;
                    break;
                default:
                    throw new Error('Invalid view type for export');
            }

            if (!fileUrl) { toast.error(`${uploadModalProps[viewType]} Excel file URL is missing!`); return; }

            const response = await fetch(fileUrl, { headers: { 'Authorization': `Bearer ${userData.accessToken}` } });
            if (!response.ok) throw new Error("File download failed");

            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `DocMaster-${uploadModalProps[viewType]}-${data.tpl_no}-${getDateString()}.xlsx`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            toast.error("Failed to download Excel: " + err.message);
        }
    }, [canView, userData]);

    // RENDER DETAIL VIEW
    const renderDetailView = (userData) => {
        if (!selectedDoc || !viewType) return null;
        const current = detailPagination[viewType];
        const filtered = getFilteredDetailList();
        const paginated = getPaginatedDetailList();
        const totalPages = getDetailTotalPages();

        return (
            <div className="detail-view-container">
                <div className="bom-header">
                    <span><strong>{uploadModalProps[viewType].toUpperCase()} DESC : </strong> {selectedDoc[`${viewType}_desc`]}</span>
                </div>
                <div className="bom-header">
                    <span><strong>TPL NO:</strong> {selectedDoc.tpl_no}</span>
                    <span><strong>DOC NO:</strong> {selectedDoc[`${viewType}_document_no`]}</span>
                    <span>
                        <strong>Date Created:</strong>{" "}
                        {new Date(selectedDoc.cd).toLocaleString("en-GB", {
                            day: "2-digit", month: "2-digit", year: "numeric",
                            hour: "2-digit", minute: "2-digit", hour12: true,
                        })}
                    </span>
                    <div className="bom-controls">
                        <SearchBox
                            value={current.searchTerm}
                            onChange={(value) => updateDetailPagination(viewType, { searchTerm: value, currentPage: 1 })}
                            onClear={() => updateDetailPagination(viewType, { searchTerm: '', currentPage: 1 })}
                            placeholder="Search..."
                        />
                        <button className="export-btn" onClick={() => openDetailReplaceModal(viewType)}>
                            <Edit2 size={16} /> Update
                        </button>
                        <button className="export-btn" onClick={() => exportToExcel(selectedDoc, viewType)}>
                            <Download size={16} /> Export
                        </button>
                    </div>
                </div>
                <div className="bom-table-container">
                    <div className="bom-sub-table-wrapper">
                        <table className="bom-table">
                            <thead>
                                <tr>
                                    {viewType === 'pdi' ? (
                                        <>
                                            <th>S.No</th>
                                            <th>Image</th>
                                            <th>Part No</th>
                                            <th>Description</th>
                                            <th>Size</th>
                                            <th>Qty</th>
                                            <th>Box Qty</th>
                                            <th>Print Text</th>
                                            <th>Unit</th>
                                            <th>Verify</th>
                                            <th>Remark</th>
                                            <th>QR</th>
                                        </>
                                    ) : (
                                        <>
                                            <th>S.No</th>
                                            <th>Inspection Parameter</th>
                                            <th>Specification</th>
                                            <th style={{ width: '50px' }}>Remark</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.map((item, idx) => (
                                    <tr key={idx}>
                                        {viewType === 'pdi' ? (
                                            <>
                                                <td>{item.sn}</td>
                                                {item.images.length > 0 ? (
                                                    <td
                                                        className="report-image-cell"
                                                        onClick={() => handleViewImages(item, 'images')}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <div
                                                            className="report-avatar"
                                                            title={`Click to view ${item.images.length} image(s)`}
                                                        >
                                                            <SecureImage
                                                                url={`${ServiceProvider.assert.get}images/pdi_master/${item.images[0]}`}
                                                                token={userData.accessToken}
                                                                alt={""}
                                                                className="report-item-image"
                                                            />
                                                        </div>
                                                    </td>
                                                ) : <td></td>}
                                                <td>{item.pn}</td>
                                                <td>{item.desc}</td>
                                                <td>{item.sz}</td>
                                                <td>{item.qty}</td>
                                                <td>{item.b_qty}</td>
                                                <td>{item.prt_txt}</td>
                                                <td>{item.unit}</td>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!item.verify_m}
                                                        disabled={!canEdit}
                                                    // onClick={(e) => { togglePdiVerifyUpdate(item, e.target.checked, viewType) }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!item.rm_man}
                                                        disabled={!canEdit}
                                                    // onClick={(e) => { togglePdiRemarkUpdate(item, e.target.checked, viewType) }}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!item.qr_m}
                                                        disabled={!canEdit}
                                                    // onClick={(e) => { togglePdiRemarkUpdate(item, e.target.checked, viewType) }}
                                                    />
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td>{item.sno}</td>
                                                <td>{item.inspection_parameter}</td>
                                                <td>{item.specification}</td>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!item.remark_m}
                                                    // onClick={(e) => { togglePdiRemarkUpdate(item, e.target.checked, viewType) }}
                                                    />
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="pagination location-pagination">
                    <div className="location-rows-per-page">
                        <span>Rows per page:</span>
                        <select
                            value={current.itemsPerPage}
                            onChange={e => updateDetailPagination(viewType, { itemsPerPage: +e.target.value, currentPage: 1 })}
                        >
                            {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                    </div>
                    <span className="location-page-info">
                        Showing {Math.min((current.currentPage - 1) * current.itemsPerPage + 1, filtered.length)} to{' '}
                        {Math.min(current.currentPage * current.itemsPerPage, filtered.length)} of{' '}
                        {filtered.length} entries
                    </span>
                    <div className="location-pagination-controls">
                        <button onClick={() => updateDetailPagination(viewType, { currentPage: 1 })} disabled={current.currentPage === 1}>⟪</button>
                        <button onClick={() => updateDetailPagination(viewType, { currentPage: current.currentPage - 1 })} disabled={current.currentPage === 1}>⟨</button>
                        {filtered.length > 0 ? (
                            <span className="location-page-numbers">Page {current.currentPage} of {totalPages}</span>
                        ) : (
                            <span className="location-page-numbers">Page 0 of 0</span>
                        )}
                        <button onClick={() => updateDetailPagination(viewType, { currentPage: current.currentPage + 1 })} disabled={current.currentPage >= totalPages}>⟩</button>
                        <button onClick={() => updateDetailPagination(viewType, { currentPage: totalPages })} disabled={current.currentPage >= totalPages}>⟫</button>
                    </div>
                </div>
            </div>
        );
    };

    // UPLOAD MODAL - UPDATED FOR EXCEL ONLY
    const UploadModal = () => {
        const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useDragAndDrop((files) => {
            if (files && files.length > 0) { setUploadModalFile(files[0]); setUploadModalError(null); }
        });

        const handleFileChange = (e) => {
            const file = e.target.files[0];
            // Validate Excel file
            if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
                setUploadModalFile(file);
                setUploadModalError(null);
            } else if (file) {
                toast.error("Only Excel files (.xlsx, .xls) are allowed");
                if (fileInputRefs.upload.current) fileInputRefs.upload.current.value = '';
            }
        };

        const handleClose = () => {
            setShowUploadModal(false); setUploadTargetDoc(null);
            setUploadModalFile(null); setUploadModalError(null);
        };

        const resetUpload = () => {
            setUploadModalFile(null); setUploadModalError(null);
            if (fileInputRefs.upload.current) fileInputRefs.upload.current.value = '';
        };

        if (!showUploadModal) return null;

        return (
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                    <div className="modal-header">
                        <h3>Upload {uploadModalProps[uploadType]} Data (Add New Items)</h3>
                        <button className="close-btn" onClick={handleClose}><X size={20} /></button>
                    </div>
                    <div className="modal-content">
                        <div className="file-upload-area">
                            <div
                                className="upload-area"
                                style={{
                                    opacity: globalLoading ? 0.6 : 1,
                                    border: `2px dashed ${isDragging ? '#2196f3' : '#ccc'}`,
                                    padding: '30px', textAlign: 'center', borderRadius: '8px',
                                    marginBottom: '15px', background: globalLoading ? '#f5f5f5' : 'white'
                                }}
                                onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver} onDrop={handleDrop}
                            >
                                <FileSpreadsheet size={48} color={isDragging ? '#2196f3' : '#666'} />
                                <h4>Drag & Drop Excel File Here</h4>
                                <p style={{ color: '#666', fontSize: '14px' }}>Only .xlsx and .xls files are accepted</p>
                                <p>or</p>
                                <input
                                    ref={fileInputRefs.upload}
                                    type="file"
                                    id="excel-upload"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    disabled={globalLoading}
                                />
                                <label
                                    htmlFor="excel-upload"
                                    className={`location-choose-file-btn ${globalLoading ? 'disabled' : ''}`}
                                    style={{ opacity: globalLoading ? 0.6 : 1, cursor: globalLoading ? 'not-allowed' : 'pointer' }}
                                >
                                    Browse Excel Files
                                </label>
                            </div>

                            {uploadModalFile && (
                                <div style={{
                                    padding: '12px',
                                    background: '#e8f5e9',
                                    border: '1px solid #4caf50',
                                    borderRadius: '6px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <FileSpreadsheet size={20} color="#4caf50" />
                                        <span style={{ fontSize: '14px', color: '#2e7d32', fontWeight: '500' }}>
                                            {uploadModalFile.name}
                                            {globalLoading && <span style={{ color: '#666', marginLeft: '8px' }}>(Uploading...)</span>}
                                        </span>
                                    </div>
                                    {!globalLoading && (
                                        <button
                                            onClick={resetUpload}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#f44336',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                padding: '0',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            title="Remove file"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            )}

                            {uploadModalFile && !globalLoading && (
                                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                                    <button
                                        onClick={() => handleUploadConfirm(uploadModalFile, uploadType)}
                                        className="btn-primary"
                                        style={{
                                            padding: '12px 24px',
                                            background: '#4caf50',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <Upload size={18} />
                                        Upload Excel Data
                                    </button>
                                </div>
                            )}

                            <div className="location-template-section" style={{ opacity: globalLoading ? 0.6 : 1 }}>
                                <span className="template-text">Need a template?</span>
                                <button
                                    onClick={() => downloadTemplate(uploadType)}
                                    className="location-download-template-btn no-link-style"
                                    style={{
                                        opacity: globalLoading ? 0.6 : 1,
                                        cursor: globalLoading ? "not-allowed" : "pointer",
                                        pointerEvents: globalLoading ? "none" : "auto",
                                        background: 'none',
                                        border: 'none',
                                        color: '#1976d2',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    Download Excel Template
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // REPLACE MODAL - UPDATED FOR EXCEL ONLY
    const ReplaceModal = () => {
        const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useDragAndDrop((files) => {
            if (files && files.length > 0) { setReplaceModalFile(files[0]); setReplaceModalError(null); }
        });

        const handleFileChange = (e) => {
            const file = e.target.files[0];
            // Validate Excel file
            if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
                setReplaceModalFile(file);
                setReplaceModalError(null);
            } else if (file) {
                toast.error("Only Excel files (.xlsx, .xls) are allowed");
                if (fileInputRefs.replace.current) fileInputRefs.replace.current.value = '';
            }
        };

        const handleClose = () => {
            setShowReplaceModal(false); setUploadTargetDoc(null);
            setReplaceModalFile(null); setReplaceModalError(null);
        };

        const resetUpload = () => {
            setReplaceModalFile(null); setReplaceModalError(null);
            if (fileInputRefs.replace.current) fileInputRefs.replace.current.value = '';
        };

        if (!showReplaceModal) return null;

        return (
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                    <div className="modal-header">
                        <h3>Replace {uploadModalProps[uploadType]} Data</h3>
                        <button className="close-btn" onClick={handleClose}><X size={20} /></button>
                    </div>
                    <div className="modal-content">
                        <div style={{
                            padding: '12px',
                            background: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '6px',
                            marginBottom: '15px',
                            color: '#856404'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <span style={{ fontSize: '16px' }}>⚠️</span>
                                <div>
                                    <strong>Warning:</strong> This will replace all existing {uploadModalProps[uploadType].toUpperCase()} items with the new data from your Excel file.
                                    <br /><small>This action cannot be undone.</small>
                                </div>
                            </div>
                        </div>

                        <div className="file-upload-area">
                            <div
                                className="upload-area"
                                style={{
                                    opacity: globalLoading ? 0.6 : 1,
                                    border: `2px dashed ${isDragging ? '#ff9800' : '#ccc'}`,
                                    padding: '30px',
                                    textAlign: 'center',
                                    borderRadius: '8px',
                                    marginBottom: '15px',
                                    background: globalLoading ? '#f5f5f5' : 'white'
                                }}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                            >
                                <RefreshCw size={48} color={isDragging ? '#ff9800' : '#666'} />
                                <h4>Drag & Drop Excel File Here</h4>
                                <p style={{ color: '#666', fontSize: '14px' }}>Only .xlsx and .xls files are accepted</p>
                                <p>or</p>
                                <input
                                    ref={fileInputRefs.replace}
                                    type="file"
                                    id="excel-replace"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    disabled={globalLoading}
                                />
                                <label
                                    htmlFor="excel-replace"
                                    className={`location-choose-file-btn ${globalLoading ? 'disabled' : ''}`}
                                    style={{ opacity: globalLoading ? 0.6 : 1, cursor: globalLoading ? 'not-allowed' : 'pointer' }}
                                >
                                    Browse Excel Files
                                </label>
                            </div>

                            {replaceModalFile && (
                                <div style={{
                                    padding: '12px',
                                    background: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    borderRadius: '6px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <FileSpreadsheet size={20} color="#ff9800" />
                                        <span style={{ fontSize: '14px', color: '#856404', fontWeight: '500' }}>
                                            {replaceModalFile.name}
                                        </span>
                                    </div>
                                    {!globalLoading && (
                                        <button
                                            onClick={resetUpload}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#f44336',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                padding: '0',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            title="Remove file"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            )}

                            {replaceModalFile && !globalLoading && (
                                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                                    <button
                                        onClick={() => handleReplaceConfirm(replaceModalFile, uploadType)}
                                        className="btn-primary"
                                        style={{
                                            padding: '12px 24px',
                                            background: '#ff9800',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <RefreshCw size={18} />
                                        Replace All Items
                                    </button>
                                </div>
                            )}

                            <div className="location-template-section" style={{ opacity: globalLoading ? 0.6 : 1 }}>
                                <span className="template-text">Need a template?</span>
                                <button
                                    onClick={() => downloadTemplate(uploadType)}
                                    className="location-download-template-btn no-link-style"
                                    style={{
                                        opacity: globalLoading ? 0.6 : 1,
                                        cursor: globalLoading ? "not-allowed" : "pointer",
                                        pointerEvents: globalLoading ? "none" : "auto",
                                        background: 'none',
                                        border: 'none',
                                        color: '#1976d2',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    Download Excel Template
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // CREATE MODAL - UPDATED FOR EXCEL ONLY
    const CreateModal = () => {
        const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useDragAndDrop((files) => {
            if (files && files.length > 0) { setCreateModalFile(files[0]); setCreateModalError(null); }
        });

        const handleFileChange = (e) => {
            const file = e.target.files[0];
            // Validate Excel file
            if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
                setCreateModalFile(file);
                setCreateModalError(null);
            } else if (file) {
                toast.error("Only Excel files (.xlsx, .xls) are allowed");
                if (fileInputRefs.create.current) fileInputRefs.create.current.value = '';
            }
        };

        const resetFile = () => {
            setCreateModalFile(null); setCreateModalError(null);
            if (fileInputRefs.create.current) fileInputRefs.create.current.value = '';
        };

        const handleClose = () => {
            setShowCreateModal(false); setCreateModalFile(null);
            setCreateModalError(null); setCreateModalType('buildup');
        };

        if (!showCreateModal) return null;

        return (
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                    <div className="modal-header">
                        <h3>Create New Document</h3>
                        <button className="close-btn" onClick={handleClose} disabled={globalLoading}><X size={20} /></button>
                    </div>
                    <div className="modal-content">
                        <div className="form-group">
                            <h4>Select Document Type:</h4>
                            <div className="radio-options" style={{ display: 'flex', flexDirection: 'row', gap: '40px', marginTop: '20px' }}>
                                {['buildup', 'test', 'pdi'].map(t => (
                                    <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="radio"
                                            value={t}
                                            checked={createModalType === t}
                                            onChange={(e) => setCreateModalType(e.target.value)}
                                            disabled={globalLoading || createModalFile}
                                        />
                                        {uploadModalProps[t]}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="file-upload-area">
                            {createModalFile && (
                                <div style={{
                                    padding: '12px',
                                    background: '#e8f5e9',
                                    border: '1px solid #4caf50',
                                    borderRadius: '6px',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <FileSpreadsheet size={20} color="#4caf50" />
                                        <span style={{ fontSize: '14px', color: '#2e7d32', fontWeight: '500' }}>
                                            {createModalFile.name}
                                            {globalLoading && <span style={{ color: '#666', marginLeft: '8px' }}>(Processing...)</span>}
                                        </span>
                                    </div>
                                    {!globalLoading && (
                                        <button
                                            onClick={resetFile}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#f44336',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                padding: '0',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            title="Remove file"
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            )}

                            {createModalFile && !globalLoading && (
                                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                                    <button
                                        onClick={() => handleCreate(createModalFile, createModalType)}
                                        className="btn-primary"
                                        style={{
                                            padding: '12px 24px',
                                            background: '#4caf50',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <Plus size={18} />
                                        Create Document
                                    </button>
                                </div>
                            )}

                            {!createModalFile && (
                                <div
                                    style={{
                                        border: `2px dashed ${isDragging ? '#2196f3' : '#ccc'}`,
                                        padding: '20px',
                                        textAlign: 'center',
                                        borderRadius: '8px',
                                        marginTop: '10px',
                                        background: globalLoading ? '#f5f5f5' : 'white',
                                        opacity: globalLoading ? 0.7 : 1
                                    }}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                >
                                    <FileSpreadsheet size={48} color={isDragging ? '#2196f3' : (globalLoading ? '#999' : '#666')} />
                                    <h4>Drag & Drop Excel File Here</h4>
                                    <p style={{ color: '#666', fontSize: '14px' }}>Only .xlsx and .xls files are accepted</p>
                                    <p>or</p>
                                    <input
                                        ref={fileInputRefs.create}
                                        type="file"
                                        id="create-excel-upload"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        disabled={globalLoading}
                                    />
                                    <label
                                        htmlFor="create-excel-upload"
                                        className="location-choose-file-btn"
                                        style={{ opacity: globalLoading ? 0.6 : 1, cursor: globalLoading ? 'not-allowed' : 'pointer' }}
                                    >
                                        Browse Excel Files
                                    </label>
                                </div>
                            )}
                        </div>

                        <div className="location-template-section" style={{ marginTop: '20px', opacity: globalLoading ? 0.6 : 1 }}>
                            <span className="template-text">Need a template?</span>
                            <button
                                onClick={() => downloadTemplate(createModalType)}
                                className="location-download-template-btn no-link-style"
                                style={{
                                    opacity: globalLoading ? 0.6 : 1,
                                    cursor: globalLoading ? "not-allowed" : "pointer",
                                    pointerEvents: globalLoading ? "none" : "auto",
                                    background: 'none',
                                    border: 'none',
                                    color: '#1976d2',
                                    textDecoration: 'underline'
                                }}
                            >
                                Download Excel Template
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // STATUS UPDATE POPUP
    const CompleteConfirmationPopup = () => {
        if (!showCompletePopup || !completeData) return null;

        return (
            <div className="modal-overlay" onClick={handleCompleteCancel}>
                <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                    <div className="modal-header">
                        <h3>STATUS UPDATE</h3>
                        <X size={24} className="close-btn" onClick={handleCompleteCancel} />
                    </div>
                    <div className="modal-content">
                        <div className="form-group" style={{ justifyContent: 'center', gap: '20px' }}>
                            <span className="approval-tpl-no">{completeData.tpl_no}</span>
                        </div>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <p style={{ fontSize: '16px', color: '#666', margin: '0' }}>
                                Are you sure you want to mark as status {completeData.status === 'active' ? "Deactive" : 'Active'}?
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button onClick={handleCompleteCancel} className="btn-cancel"
                                style={{ background: 'linear-gradient(135deg, #6c757d, #868e96)', flex: 1, maxWidth: '120px' }}>
                                Cancel
                            </button>
                            <button onClick={docStatusUpdate} className="btn-primary"
                                style={{ background: 'linear-gradient(135deg, #ff8c00, #ff9f26)', flex: 1, maxWidth: '120px' }}>
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // STATUS UPDATE POPUP
    const DeleteConfirmationPopup = () => {
        if (!showDeletePopup || !deleteData) return null;

        return (
            <div className="modal-overlay" onClick={handleDeleteCancel}>
                <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                    <div className="modal-header">
                        <h3>DELETE DOCUMENT</h3>
                        <X size={24} className="close-btn" onClick={handleDeleteCancel} />
                    </div>
                    <div className="modal-content">

                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <p style={{ fontSize: '16px', color: '#666', margin: '0' }}>
                                Are you sure you want to delete this {deleteData.tpl_no} document?
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button onClick={handleDeleteCancel} className="btn-cancel"
                                style={{ background: 'linear-gradient(135deg, #6c757d, #868e96)', flex: 1, maxWidth: '120px' }}>
                                Cancel
                            </button>
                            <button onClick={docDelete} className="btn-primary"
                                style={{ background: 'linear-gradient(135deg, #ff8c00, #ff9f26)', flex: 1, maxWidth: '120px' }}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Handle search clear explicitly
    const handleSearchClear = useCallback(() => {
        setSearchTerm('');
        // The useEffect will handle fetching normal data when searchTerm becomes empty
    }, []);

    // MAIN RENDER
    return (
        <div className="bom-container">
            <Breadcrumbs backNav={backNav} />
            {selectedDoc ? renderDetailView(userData) : (
                <>
                    <div className="out-bom-header">
                        <div className="bom-controls">
                            <SearchBox
                                value={searchTerm}
                                onChange={setSearchTerm}
                                onClear={handleSearchClear}
                                placeholder="Search... (min 3 chars)"
                            />
                            {canAdd && (
                                <button className="add-bom-btn" onClick={() => setShowCreateModal(true)}>Add DOC</button>
                            )}
                        </div>
                    </div>

                    <div className="bom-table-container">
                        <div className="bom-table-wrapper">
                            <table className="bom-table">
                                <thead>
                                    <tr>
                                        <th>S NO</th>
                                        <th>TPL NO</th>
                                        <th>DATE</th>
                                        <th>{uploadModalProps['buildup']}</th>
                                        <th>{uploadModalProps['test']}</th>
                                        <th>{uploadModalProps['pdi']}</th>
                                        <th>MODIFIED</th>
                                        <th>EMPLOYEE</th>
                                        {canEdit && <th>STATUS / ACTION</th>}
                                        {userData?.role !== 'user' && <th>Delete</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {docList.map((doc, idx) => (
                                        <tr key={doc._id}>
                                            <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                            <td>{doc.tpl_no}</td>
                                            <td>
                                                {doc.cd ? new Date(doc.cd).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'}
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    {doc.buildup_items?.length > 0 ? (
                                                        <button className="view-btn" onClick={() => { addCustomPath(`${uploadModalProps['buildup']}`); handleViewSection(doc, 'buildup'); }} title={`View ${uploadModalProps['buildup']} Data`}><Eye size={18} /></button>
                                                    ) : (
                                                        <button className={`upload-btn-small ${!canUpload ? 'disabled' : ''}`} onClick={() => canUpload && openUploadModal(doc, 'buildup')} disabled={!canUpload} style={{ opacity: !canUpload ? 0.5 : 1, cursor: !canUpload ? 'not-allowed' : 'pointer' }}><Upload size={18} /></button>
                                                    )}
                                                    <input type="checkbox" checked={doc.buildup_mandated} onChange={() => canEdit && toggleMandatedInList(doc, 'buildup')} disabled={!canEdit || !doc.buildup_items?.length > 0} style={{ opacity: (!canEdit || !doc.buildup_items?.length > 0) ? 0.5 : 1, cursor: (!canEdit || !doc.buildup_items?.length > 0) ? 'not-allowed' : 'pointer' }} />
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    {doc.test_items?.length > 0 ? (
                                                        <button className="view-btn" onClick={() => { addCustomPath(`${uploadModalProps['test']}`); handleViewSection(doc, 'test'); }} title={`View ${uploadModalProps['test']} Data`}><Eye size={18} /></button>
                                                    ) : (
                                                        <button className={`upload-btn-small ${!canUpload ? 'disabled' : ''}`} onClick={() => canUpload && openUploadModal(doc, 'test')} disabled={!canUpload} style={{ opacity: !canUpload ? 0.5 : 1, cursor: !canUpload ? 'not-allowed' : 'pointer' }}><Upload size={18} /></button>
                                                    )}
                                                    <input type="checkbox" checked={doc.test_mandated} onChange={() => canEdit && toggleMandatedInList(doc, 'test')} disabled={!canEdit || !doc.test_items?.length > 0} style={{ opacity: (!canEdit || !doc.test_items?.length > 0) ? 0.5 : 1, cursor: (!canEdit || !doc.test_items?.length > 0) ? 'not-allowed' : 'pointer' }} />
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    {doc.pdi_items?.length > 0 ? (
                                                        <button className="view-btn" onClick={() => { addCustomPath('PDI'); handleViewSection(doc, 'pdi'); }} title="View PDI Data"><Eye size={18} /></button>
                                                    ) : (
                                                        <button className={`upload-btn-small ${!canUpload ? 'disabled' : ''}`} onClick={() => canUpload && openUploadModal(doc, 'pdi')} disabled={!canUpload} style={{ opacity: !canUpload ? 0.5 : 1, cursor: !canUpload ? 'not-allowed' : 'pointer' }}><Upload size={18} /></button>
                                                    )}
                                                    <input type="checkbox" checked={doc.pdi_mandated} onChange={() => canEdit && toggleMandatedInList(doc, 'pdi')} disabled={!canEdit || !doc.pdi_items?.length > 0} style={{ opacity: (!canEdit || !doc.pdi_items?.length > 0) ? 0.5 : 1, cursor: (!canEdit || !doc.pdi_items?.length > 0) ? 'not-allowed' : 'pointer' }} />
                                                </div>
                                            </td>
                                            <td>{doc.md ? new Date(doc.md).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }) : "N/A"}</td>
                                            <td>{userList.find((e) => e.uid === doc.employee_id) ? `${userList.find((e) => e.uid === doc.employee_id).name} - (${doc.employee_id.slice(-6)})` : "N/A"}</td>
                                            {canEdit && (
                                                <td>
                                                    <span onClick={() => { if (canEdit) { setShowCompletePopup(true); setCompleteData(doc); } }} className={`status ${doc.status}`} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
                                                        {doc.status}
                                                    </span>
                                                </td>
                                            )}
                                            {userData?.role !== 'user' && (<td>
                                                <button
                                                    onClick={() => {
                                                        setShowDeletePopup(true);
                                                        setDeleteData(doc);
                                                    }}
                                                    style={{
                                                        backgroundColor: '#ff4d4f',   // red background
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        padding: '3px 4px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',

                                                        transition: '0.2s ease-in-out'
                                                    }}
                                                >
                                                    <Trash2 size={18} color="#fff" />
                                                </button>
                                            </td>)}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="location-pagination">
                        <div className="location-rows-per-page">
                            <span>Rows per page:</span>
                            <select value={itemsPerPage} onChange={handlePageSizeChange}>
                                {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        <span className="location-page-info">
                            {totalCount > 0 ? (
                                <>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries</>
                            ) : <>Showing 0 to 0 of 0 entries</>}
                        </span>
                        <div className="location-pagination-controls">
                            <button onClick={handleFirstPage} disabled={currentPage === 1 || loading}>⟪</button>
                            <button onClick={handlePreviousPage} disabled={currentPage === 1 || loading}>⟨</button>
                            {totalCount > 0 ? (
                                <span className="location-page-numbers">Page {currentPage} of {totalPages}</span>
                            ) : (
                                <span className="location-page-numbers">Page 0 of 0</span>
                            )}
                            <button onClick={handleNextPage} disabled={currentPage >= totalPages || loading}>⟩</button>
                            <button onClick={handleLastPage} disabled={currentPage >= totalPages || loading}>⟫</button>
                        </div>
                    </div>
                </>
            )}

            <UploadModal />
            <ReplaceModal />
            <CreateModal />
            {showCompletePopup && <CompleteConfirmationPopup />}
            {showDeletePopup && <DeleteConfirmationPopup />}

            <ImageDisplayPopup userData={userData} isOpen={showImageDisplayPopup} onClose={handleCloseImageDisplayPopup} pdiItem={selectedPdiItemForImages} />

            {/* Full-Screen Zoomable Image Viewer */}
            <FullScreenImageViewer
                isOpen={fullScreenViewer.isOpen}
                onClose={handleCloseFullScreenViewer}
                images={fullScreenViewer.images}
                initialIndex={fullScreenViewer.initialIndex}
                viewType={fullScreenViewer.viewType}
                userData={userData}
            />

            <GlobalLoadingOverlay isLoading={globalLoading} message={loadingMessage} />
        </div>
    );
};

export default DocMasterListView;